import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import {
  mockForumTopics,
  mockForumReplies,
  type ForumTopic,
  type ForumReply,
} from '@/mocks/forum';
import {
  mapDbForumTopic,
  mapDbForumReply,
  formatViews,
  timeAgo,
  initialsOf,
  type ForumTopicDbRow,
  type ForumReplyDbRow,
} from '@/lib/forum';

function ForumDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [topic, setTopic] = useState<ForumTopic | null>(null);
  const [replies, setReplies] = useState<ForumReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('forum_topics')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      if (err) throw err;

      if (data) {
        const row = data as ForumTopicDbRow;
        const { data: ownerProfile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', row.owner_id)
          .maybeSingle();

        const { data: replyRows, error: replyErr } = await supabase
          .from('forum_replies')
          .select('*')
          .eq('topic_id', id)
          .eq('status', 'active')
          .order('created_at', { ascending: true });
        if (replyErr) throw replyErr;

        const rows = (replyRows as ForumReplyDbRow[]) || [];
        const userIds = Array.from(new Set(rows.map((r) => r.user_id)));
        const { data: replyProfiles } = userIds.length
          ? await supabase.from('profiles').select('id, full_name').in('id', userIds)
          : { data: [] };
        const nameMap: Record<string, string> = {};
        (replyProfiles || []).forEach((p) => {
          nameMap[p.id] = p.full_name;
        });

        setTopic(
          mapDbForumTopic(
            row,
            ownerProfile?.full_name || '',
            rows.length,
          ),
        );
        setReplies(
          rows.map((r) => {
            const name = nameMap[r.user_id] || '';
            return mapDbForumReply(r, name, initialsOf(name));
          }),
        );
      } else {
        // Fallback mock si introuvable
        const mock = mockForumTopics.find((t) => t.id === id) || null;
        setTopic(mock);
        setReplies(mockForumReplies[id] || []);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger la discussion.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/auth/login');
      return;
    }
    if (!replyText.trim()) return;

    setSubmitting(true);
    setSubmitError('');

    const { error: err } = await supabase.from('forum_replies').insert({
      topic_id: id,
      user_id: user.id,
      content: replyText.trim(),
    });

    setSubmitting(false);

    if (err) {
      setSubmitError(err.message);
      return;
    }

    setReplyText('');
    await load();
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6 flex-wrap">
            <Link to="/forum" className="hover:text-foreground-50 transition-colors">
              Forum
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400 truncate">
              {topic?.title || 'Discussion'}
            </span>
          </nav>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-500/10">
                <i className="ri-loader-4-line text-2xl text-secondary-400 animate-spin" />
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-foreground-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap"
              >
                Réessayer
              </button>
            </div>
          ) : !topic ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-secondary-500/10">
                <i className="ri-chat-3-line text-3xl text-secondary-400" />
              </div>
              <h2 className="font-heading font-bold text-xl text-foreground-50 mb-2">
                Discussion introuvable
              </h2>
              <p className="text-sm text-foreground-400 mb-6">
                Cette discussion n&apos;existe pas ou a été supprimée.
              </p>
              <Link
                to="/forum"
                className="inline-block px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm"
              >
                Retour au forum
              </Link>
            </div>
          ) : (
            <>
              {/* Topic */}
              <article className="rounded-xl bg-background-100 border border-background-200/70 p-6 mb-6">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-secondary-500/10 text-secondary-400 whitespace-nowrap">
                    {topic.category}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {formatViews(topic.views)}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-chat-3-line" />
                    {topic.repliesCount} réponse{topic.repliesCount > 1 ? 's' : ''}
                  </span>
                </div>

                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-4 leading-tight">
                  {topic.title}
                </h1>

                <div className="flex items-center gap-3 mb-5 pb-5 border-b border-background-200/70">
                  <div className="w-11 h-11 rounded-full bg-secondary-500/10 flex items-center justify-center shrink-0">
                    <span className="text-sm font-semibold text-secondary-400">
                      {initialsOf(topic.authorName)}
                    </span>
                  </div>
                  <div>
                    <span className="block text-sm font-semibold text-foreground-50">
                      {topic.authorName}
                    </span>
                    <span className="block text-xs text-foreground-400">
                      Publié {timeAgo(topic.createdAt)}
                    </span>
                  </div>
                </div>

                <p className="text-foreground-50 leading-relaxed whitespace-pre-line">
                  {topic.body}
                </p>
              </article>

              {/* Replies */}
              <section>
                <h2 className="font-heading font-semibold text-lg text-foreground-50 mb-4 flex items-center gap-2">
                  <i className="ri-chat-3-line text-secondary-400" />
                  Réponses ({replies.length})
                </h2>

                {replies.length === 0 ? (
                  <div className="rounded-xl bg-background-100 border border-background-200/70 p-8 text-center">
                    <p className="text-sm text-foreground-400">
                      Aucune réponse pour le moment. Soyez le premier à répondre !
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3 mb-8">
                    {replies.map((reply) => (
                      <div
                        key={reply.id}
                        className="rounded-xl bg-background-100 border border-background-200/70 p-5"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 rounded-full bg-accent-500/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-accent-400">
                              {reply.authorInitials}
                            </span>
                          </div>
                          <div>
                            <span className="block text-sm font-semibold text-foreground-50">
                              {reply.authorName}
                            </span>
                            <span className="block text-xs text-foreground-400">
                              {timeAgo(reply.createdAt)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-foreground-50 leading-relaxed whitespace-pre-line">
                          {reply.content}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply form */}
                <div className="rounded-xl bg-background-100 border border-background-200/70 p-6">
                  <h3 className="font-heading font-semibold text-foreground-50 mb-4">
                    Répondre à la discussion
                  </h3>

                  {user ? (
                    <form onSubmit={handleSubmitReply} className="space-y-4">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={4}
                        maxLength={2000}
                        placeholder="Partagez votre réponse..."
                        className="w-full bg-background-50 border border-background-300/60 rounded-md px-4 py-3 text-sm text-foreground-50 placeholder:text-foreground-700 outline-none focus:border-secondary-400 resize-none"
                      />
                      {submitError && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                          {submitError}
                        </div>
                      )}
                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={submitting || !replyText.trim()}
                          className="inline-flex items-center gap-2 px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
                        >
                          <i className="ri-send-plane-line" />
                          {submitting ? 'Envoi...' : 'Publier la réponse'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-foreground-400 mb-4">
                        Connectez-vous pour participer à la discussion.
                      </p>
                      <Link
                        to="/auth/login"
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-secondary-500 text-foreground-950 font-semibold rounded-lg hover:bg-secondary-400 transition-colors text-sm whitespace-nowrap"
                      >
                        <i className="ri-login-box-line" />
                        Se connecter
                      </Link>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default ForumDetailPage;